import {
    ApiShopperLandingPageLink,
    VLPVariant,
} from '@storefronts/common-types'
import { Cart, VariantWithImage } from '../types'
import { runWhenReady } from '../components/pixels/pixelRuntime'

declare global {
    interface Window {
        ElevarDataLayer: any
    }
}

type ElProduct = {
    id: string // SKU
    name: string // Product title
    brand: string
    category: string
    variant: string
    price: string
    quantity: string // Not required for dl_select_item & dl_view_item
    position: number // Only required for dl_select_item; position in the list of search results, collection views and position in cart indexed starting at 1
    list: string // The list the product was discovered from
    product_id: string // The product_id
    variant_id: string // id or variant_id
    compare_at_price: string // If available on dl_view_item & dl_add_to_cart otherwise use "0.0"
    image: string // If available, otherwise use an empty string
    inventory: string // If available, only required on dl_view_item
}

const landingPageLinkToElProduct = (
    link: ApiShopperLandingPageLink,
    variant: VLPVariant | null,
    quantity: number | null,
    position: number | null,
): ElProduct => {
    return {
        id: '', // SKU
        name: link.title, // Product title
        brand: 'Michael Todd Beauty',
        category: 'Beauty Tools',
        variant: variant?.title || link.title,
        price: variant
            ? `${(variant.price.price / 100).toFixed(2)}`
            : link.price.price
              ? `${(link.price.price / 100).toFixed(2)}`
              : '',
        quantity: quantity ? `${quantity}` : '',
        position: position ? position : 0,
        list: 'viddy',
        product_id: `${link.shopifyId}`,
        variant_id: variant ? `${variant.shopifyId}` : `${link.shopifyId}`,
        compare_at_price: variant
            ? variant.price.compareAtPrice
                ? `${(variant.price.compareAtPrice / 100).toFixed(2)}`
                : link.price.compareAtPrice
                  ? `${(link.price.compareAtPrice / 100).toFixed(2)}`
                  : ''
            : '',
        image: '',
        inventory: '',
    }
}

const userProperties = {
    user_consent: '', // Use an empty string
    visitor_type: 'guest', // "logged_in" || "guest"
}

export const EL_USER_DATA = (cart: Cart) => {
    runWhenReady('elevar', () => {
        if (!window.ElevarDataLayer) return

        const items = cart.items.map((item, index) => {
            const link = item.variant.link
            const variant = item.variant
            return landingPageLinkToElProduct(
                link,
                variant,
                item.quantity,
                index + 1,
            )
        })

        let total = 0
        cart.items.forEach((item) => {
            total += item.variant.finalPrice * item.quantity
        })

        window.ElevarDataLayer.push({
            event: 'dl_user_data',
            cart_total: `${(total / 100).toFixed(2)}`,
            user_properties: userProperties, // See user properties object below
            ecommerce: {
                currencyCode: 'USD',
                cart_contents: {
                    products: items, // See the products array below
                },
            },
        })
    })
}

export const EL_VIEW_ITEM = (
    link: ApiShopperLandingPageLink,
    variant: VLPVariant,
) => {
    runWhenReady('elevar', () => {
        if (!window.ElevarDataLayer) return

        const products = [landingPageLinkToElProduct(link, variant, null, null)]

        window.ElevarDataLayer.push({
            event: 'dl_view_item',
            user_properties: userProperties,
            ecommerce: {
                currencyCode: 'USD',
                detail: {
                    actionField: {
                        list: 'viddy', // this should be the collection page URL that user clicked product from
                        action: 'detail',
                    },
                    products: products, // See the products array below
                },
            },
        })
    })
}

export const EL_SELECT_ITEM = (link: ApiShopperLandingPageLink) => {
    runWhenReady('elevar', () => {
        if (!window.ElevarDataLayer) return

        const products = [landingPageLinkToElProduct(link, null, null, null)]

        window.ElevarDataLayer.push({
            event: 'dl_select_item',
            user_properties: userProperties,
            ecommerce: {
                currencyCode: 'USD',
                click: {
                    actionField: {
                        list: 'viddy', // this should be the collection page URL
                        action: 'click',
                    },
                    products: products, // See the products array below
                },
            },
        })
    })
}

export const EL_VIEW_CART = (cart: Cart) => {
    runWhenReady('elevar', () => {
        if (!window.ElevarDataLayer) return

        const items = cart.items.map((item, index) => {
            const link = item.variant.link
            const variant = item.variant
            return landingPageLinkToElProduct(
                link,
                variant,
                item.quantity,
                index + 1,
            )
        })

        let total = 0
        cart.items.forEach((item) => {
            total += item.variant.finalPrice * item.quantity
        })

        window.ElevarDataLayer.push({
            event: 'dl_view_cart',
            user_properties: userProperties,
            cart_total: `${(total / 100).toFixed(2)}`,
            ecommerce: {
                currencyCode: 'USD',
                actionField: {
                    list: 'Shopping Cart',
                },
                impressions: items, // See impressions array below
            },
        })
    })
}

export const EL_ADD_TO_CART = (variant: VariantWithImage, quantity: number) => {
    runWhenReady('elevar', () => {
        if (!window.ElevarDataLayer) return

        const products = [
            landingPageLinkToElProduct(variant.link, variant, quantity, null),
        ]

        window.ElevarDataLayer.push({
            event: 'dl_add_to_cart',
            user_properties: userProperties,
            ecommerce: {
                currencyCode: 'USD',
                add: {
                    actionField: {
                        list: 'viddy', // this should be the collection page URL that user clicked product from
                    },
                    products: products, // See the products array below
                },
            },
        })
    })
}

export const EL_REMOVE_FROM_CART = (variant: VariantWithImage) => {
    runWhenReady('elevar', () => {
        if (!window.ElevarDataLayer) return

        const products = [
            landingPageLinkToElProduct(variant.link, variant, null, null),
        ]

        window.ElevarDataLayer.push({
            event: 'dl_remove_from_cart',
            user_properties: userProperties,
            ecommerce: {
                currencyCode: 'USD',
                remove: {
                    actionField: {
                        list: 'viddy', // this should be the collection page URL that user clicked product from
                    },
                    products: products, // See the products array below
                },
            },
        })
    })
}
